import { useMemo } from 'react'
import { useAuth } from '../../../../../../../auth'
import { TableState } from '../../../../../../../hooks/use-table-state'
import { UserPermissions } from '../../../../../../../types/permissions'
import { RouteMode } from '../../../../../../../types/route'
import { FormElementConfig } from '../../../../../../../forms/types'
import { toDollarCurrencyFormat } from '../../../../../../../forms/utils'
import { ApplicationDetailsFields } from './config'
import { GATE_TROUGH_TABLE_COLUMNS } from '../../../../../../../forms/constants'

interface GateTroughConfigProps extends Pick<TableState, 'refreshTable' | 'deleteAction'> {
  mode?: RouteMode
  isMigrated?: boolean
  planId?: string
  applicationId?: string
}

export const useGateTroughConfig = ({
  mode,
  refreshTable,
  isMigrated,
  planId,
  applicationId,
  deleteAction,
}: GateTroughConfigProps): FormElementConfig<ApplicationDetailsFields>[] => {
  const { user } = useAuth()
  return useMemo(() => {
    if (isMigrated)
      return [
        {
          type: 'field',
          element: {
            type: 'repeating-section',
            heading: 'Gates and troughs',
            sectionName: 'Gates and troughs action',
            repeating: true,
            fieldId: 'gateTroughActions',
            fields: [
              {
                type: 'lookup',
                fieldId: 'type',
                lookupId: 'action_subtype',
                fieldFilters: { feature_category: 'gate_trough' },
              },
              {
                type: 'number',
                fieldId: 'unitCount',
              },
              {
                type: 'number',
                fieldId: 'proposedCost',
              },
            ],
          },
        },
      ]
    return [
      {
        type: 'atom',
        element: { type: 'heading', level: 2, content: 'Gates and troughs' },
      },
      {
        type: 'field',
        element: {
          type: 'table',
          currentUserPermissions: user?.permissions,
          columnHeaders: [
            ...GATE_TROUGH_TABLE_COLUMNS,
            {
              elementKey: 'actions',
              columnHeading: '',
              viewColumn: 'actions',
              type: 'actions-no-data',
              actions:
                mode == RouteMode.EDIT
                  ? [
                      {
                        label: 'Delete',
                        type: 'deleteAction',
                        args: [
                          {
                            elementKey: 'id',
                          },
                        ],
                        actionPermissions: [UserPermissions.EDIT_PLANS],
                      },
                    ]
                  : [],
            },
          ],
          refreshTable: refreshTable,
          tableActionHandlers: {
            deleteAction,
          },
          defaultSortBy: {
            orderColumn: 'locationName',
            orderDirection: 'desc',
          },
          requestUrl: `/api/plan/${planId}/project/${applicationId}/feature/gate-trough`,
          rowClickAction: {
            label: 'View',
            type: 'transitionAction',
            args: [{ constant: `plans/${mode}/${planId}/plan/project/${applicationId}/action` }, { elementKey: 'id' }],
          },
          noResultsMessage: 'No gates and troughs actions added',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.proposedGatesUnits',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.proposedGatesCost',
          conversion: toDollarCurrencyFormat,
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.proposedTroughUnits',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.proposedTroughCost',
          conversion: toDollarCurrencyFormat,
        },
      },
    ]
  }, [mode, refreshTable, isMigrated, planId, applicationId, deleteAction])
}
